/**
 * Partners — no prototype counterpart, same category of deviation as Teams
 * and Email (see those modules' own header notes). `crm.partner` has existed
 * in the schema since db/schema/07 (§27 Referral and Partner Management)
 * with no screen until docs/modules/partnerLeads.model.md §3.
 *
 * Internal-only (manager/superadmin) — the `partner` role this feature also
 * adds never sees this screen, it only submits leads.
 */
import { useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { Badge, SearchInput, ChipSelect, ErrorState } from '../../components/common/index.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { number } from '../../utils/formatters.js';
import { usePartners, useCreatePartner, useUpdatePartner } from './usePartners.js';
import { PartnerFormModal } from './PartnerModals.jsx';

const PARTNER_TYPES = ['Reseller', 'Referral', 'Technology', 'Implementation', 'Distributor'];

export default function PartnersAdminPage() {
  const { can } = useAuth();
  const canCreate = can('admin.partners', 'create');
  const canEdit = can('admin.partners', 'edit');

  const [search, setSearch] = useState('');
  const [partnerType, setPartnerType] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const debounced = useDebounce(search);
  const pages = useCursorPages(25);

  const params = useMemo(() => ({
    ...pages.params,
    q: debounced || undefined,
    partner_type: partnerType || undefined,
  }), [pages.params, debounced, partnerType]);

  const { data, isLoading, error, refetch } = usePartners(params);
  const rows = data?.data ?? [];

  const createMutation = useCreatePartner();
  const updateMutation = useUpdatePartner();

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };
  const openEdit = (partner) => { setEditing(partner); setFormOpen(true); };
  const openCreate = () => { setEditing(null); setFormOpen(true); };

  const columns = [
    {
      key: 'name', header: 'Partner',
      render: (p) => (
        <div className="min-w-0">
          <button type="button" className="btn btn-link p-0 fw-600 text-truncate" onClick={() => canEdit && openEdit(p)}>
            {p.name}
          </button>
        </div>
      ),
    },
    { key: 'type', header: 'Type', width: 140, render: (p) => p.partner_type ?? <span className="text-muted">—</span> },
    { key: 'account', header: 'Linked account', width: 180, render: (p) => p.account?.name ?? <span className="text-muted">—</span> },
    { key: 'contact', header: 'Primary contact', width: 180, render: (p) => p.primary_contact?.name ?? <span className="text-muted">—</span> },
    { key: 'commission', header: 'Commission', width: 110, render: (p) => (p.commission_pct != null ? `${p.commission_pct}%` : <span className="text-muted">—</span>) },
    {
      key: 'status', header: 'Status', width: 100,
      render: (p) => <Badge tone={p.is_active ? 'emerald' : 'neutral'}>{p.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb"><span>Administration</span><i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" /><span>Partners</span></div>
          <h1 className="page-title">Partners</h1>
          <p className="page-sub">{number(data?.page?.total ?? rows.length)} partner companies</p>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2">
          {canCreate && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true" />Register partner
            </button>
          )}
        </div>
      </div>

      <div className="card-x fade-up">
        <div className="d-flex flex-wrap align-items-center gap-2 p-3 border-bottom">
          <div style={{ maxWidth: 240 }}>
            <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search partners…" />
          </div>
          <ChipSelect label="Type" allLabel="All types" value={partnerType} onChange={onFilter(setPartnerType)} options={PARTNER_TYPES} />
        </div>

        {error ? (
          <div className="p-4"><ErrorState error={error} onRetry={refetch} /></div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            isLoading={isLoading}
            page={data?.page}
            onNext={pages.goNext}
            onPrevious={pages.goPrevious}
            canGoPrevious={pages.canGoPrevious}
            onRowClick={canEdit ? openEdit : undefined}
            emptyTitle="No partners match these filters"
          />
        )}
      </div>

      <PartnerFormModal
        open={formOpen}
        partner={editing}
        busy={createMutation.isPending || updateMutation.isPending}
        onClose={() => setFormOpen(false)}
        onSubmit={async (payload) => {
          if (editing) {
            await updateMutation.mutateAsync({ id: editing.id, patch: payload, rowVersion: editing.row_version });
          } else {
            await createMutation.mutateAsync(payload);
          }
          setFormOpen(false);
        }}
      />
    </>
  );
}
